import { SplitSupplyNoticeSection } from '@ebalance/proto/bffuser/v1/split_supply_plan_model_pb'

import {
  type CivilDate,
  civilDateToDate,
  dateToCivilDate,
} from '~/models/civilDate'
import { addDays, formatDateDisplay } from '~/models/dateTime'

/**
 * 分割供給計画の対象期間の画面モデル。 翌日の通は受渡日 1 日、 月間の通は対象月 (月の全日)。
 * 月間の対象日は常に月の 1 日で持つ。 作成・編集できる範囲の判断は backend が持ち
 * (NOTICE_DATE_CLOSED)、 ここの `earliestEditableTarget` は選べる範囲を絞る UX のための同じ規則。
 */

export const SECTION_TABS = ['dayAhead', 'monthly'] as const
export type SectionTab = (typeof SECTION_TABS)[number]

export const SECTION_OF_TAB: Record<SectionTab, SplitSupplyNoticeSection> = {
  dayAhead: SplitSupplyNoticeSection.DAY_AHEAD,
  monthly: SplitSupplyNoticeSection.MONTHLY,
}

const firstOfMonth = (d: CivilDate): CivilDate => ({
  year: d.year,
  month: d.month,
  day: 1,
})

/** 月を delta だけ進めた月の 1 日 (年をまたぐ)。 */
export function addMonths(d: CivilDate, delta: number): CivilDate {
  const index = d.year * 12 + (d.month - 1) + delta
  return { year: Math.floor(index / 12), month: (index % 12) + 1, day: 1 }
}

/** 一覧の対象日の初期値: 翌日の通は明日、 月間の通は来月の 1 日。 */
export function defaultTarget(tab: SectionTab, today: CivilDate): CivilDate {
  return tab === 'monthly'
    ? addMonths(today, 1)
    : dateToCivilDate(addDays(civilDateToDate(today), 1))
}

/** 対象日を 1 つ送る (翌日の通は 1 日、 月間の通は 1 か月)。 */
export function stepTarget(
  tab: SectionTab,
  target: CivilDate,
  delta: 1 | -1,
): CivilDate {
  return tab === 'monthly'
    ? addMonths(target, delta)
    : dateToCivilDate(addDays(civilDateToDate(target), delta))
}

/** 作成・編集できる最も早い対象日 (翌日の通は明日、 月間の通は来月の 1 日)。 backend の規則と同じ。 */
export function earliestEditableTarget(
  tab: SectionTab,
  today: CivilDate,
): CivilDate {
  return defaultTarget(tab, today)
}

/** 対象月の選択肢の値 (`DsSelect` の value)。 `parseMonthOptionValue` で月の 1 日に戻す。 */
export function monthOptionValue(month: CivilDate): string {
  return `${month.year}-${month.month}`
}

/** 対象月の選択肢の値から月の 1 日。 読めなければ undefined。 */
export function parseMonthOptionValue(
  value: string | undefined,
): CivilDate | undefined {
  const [year, month] = (value ?? '').split('-').map(Number)
  if (!Number.isInteger(year) || !Number.isInteger(month)) return undefined
  return { year, month, day: 1 }
}

/** 詳細の見出しの日付の表記。 無ければ「—」。 */
export function formatCivilDay(d: CivilDate | undefined): string {
  return d === undefined ? '—' : formatDateDisplay(civilDateToDate(d))
}

/** 対象月の全日 (1 日から月末まで)。 */
export function daysOfMonth(target: CivilDate): CivilDate[] {
  const first = firstOfMonth(target)
  const days: CivilDate[] = []
  for (let d = first; d.month === first.month; ) {
    days.push(d)
    d = dateToCivilDate(addDays(civilDateToDate(d), 1))
  }
  return days
}

/** 月間の通で、 表示する日を対象月の中に収める (範囲外なら月初)。 */
export function clampToMonth(day: CivilDate, target: CivilDate): CivilDate {
  return day.year === target.year && day.month === target.month
    ? day
    : firstOfMonth(target)
}
